(function () {
  'use strict';

  /* ============================================================
     WAND — Collection infinite scroll
     ------------------------------------------------------------
     Page 1 renders server-side. The section emits an explicit
     [data-wand-infinite] state element carrying data-current-page,
     data-total-pages and data-next-url (Shopify's paginate object —
     next-url already includes the active filter/sort params). Every
     fetched page's state is re-parsed and validated the same way:
     EOF is only ever declared when currentPage >= totalPages. A
     response that doesn't carry that state (or reports a page still
     behind totalPages with no next-url) is never treated as EOF —
     it's a recoverable failure, retried a bounded number of times,
     falling back to native pagination if it never recovers.

     A generation counter is bumped on every init() (initial load and
     every facets.js filter/sort re-render). Each in-flight fetch
     captures the generation it started under; any DOM write or state
     mutation is gated on that generation still being current, so a
     response for a grid that has since been replaced can never touch
     the new grid or state. The trigger element WRAPS the loader so it
     is never :empty — base.css has a global `div:empty { display:none }`
     reset that collapses empty elements, which is what removed the
     original standalone sentinel from layout and stopped the
     IntersectionObserver from ever firing.
     ============================================================ */

  var RETRY_LIMIT = 2;
  var RETRY_DELAY_MS = 600;

  var generation = 0;
  var isLoading = false;
  var finished = false;
  var nextHref = null;
  var observer = null;
  var controller = null;                 // AbortController for the in-flight fetch
  var fetched = Object.create(null);     // de-dupe fetched pages, keyed by generation|url
  var retryCounts = Object.create(null); // retry attempts used, keyed by generation|url

  /* ── DOM helpers ─────────────────────────────────────── */

  function gridEl()    { return document.getElementById('product-grid'); }
  function triggerEl() { return document.querySelector('[data-wand-infinite]'); }
  function loaderEl()  { return document.querySelector('.wand-infinite-loader'); }

  /* Parse + validate the pagination state carried by [data-wand-infinite].
     Anything that doesn't parse into a coherent state is "invalid" —
     never trusted as proof of end-of-results. */
  function readState(scope) {
    var el = (scope || document).querySelector('[data-wand-infinite]');
    if (!el) return { valid: false };

    var currentPage = parseInt(el.getAttribute('data-current-page'), 10);
    var totalPages = parseInt(el.getAttribute('data-total-pages'), 10);
    var rawNext = el.getAttribute('data-next-url');
    var nextUrl = rawNext && rawNext.trim() ? rawNext.trim() : null;

    if (!isFinite(currentPage) || !isFinite(totalPages) || currentPage < 1 || totalPages < 1) {
      return { valid: false };
    }

    return { valid: true, currentPage: currentPage, totalPages: totalPages, nextUrl: nextUrl };
  }

  function requestedPageFromUrl(url) {
    try {
      var params = new URL(url, window.location.origin).searchParams;
      var raw = params.get('page');
      var page = raw ? parseInt(raw, 10) : NaN;
      return isFinite(page) && page > 0 ? page : null;
    } catch (e) {
      return null;
    }
  }

  /* ── UI state ────────────────────────────────────────── */

  function showLoader(on) {
    var el = loaderEl();
    if (el) el.classList.toggle('wand-infinite-loader--visible', !!on);
  }

  function hidePagination() {
    document.querySelectorAll('.wand-pagination-native').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
      el.style.display = 'none';
    });
  }

  function showNativePagination() {
    document.querySelectorAll('.wand-pagination-native').forEach(function (el) {
      el.removeAttribute('aria-hidden');
      el.style.display = '';
    });
  }

  function finish() {
    finished = true;
    nextHref = null;
    showLoader(false);
    if (observer) { observer.disconnect(); observer = null; }

    var g = gridEl();
    if (!g) return;
    var host = g.parentElement;
    if (host && !host.querySelector('.wand-infinite-end')) {
      var t = triggerEl();
      var msg = (t && t.getAttribute('data-all-seen-text')) || 'You’ve seen all the designs ✨';
      var end = document.createElement('p');
      end.className = 'wand-infinite-end';
      end.setAttribute('aria-live', 'polite');
      end.textContent = msg;
      host.appendChild(end);
    }
  }

  /* Recoverable failure (network error, or a fetched page whose
     pagination state is missing/inconsistent). Never declares EOF —
     retries a bounded number of times, then hands off to native
     pagination so no product is ever silently hidden. */
  function handleRecoverable(myGeneration, key) {
    if (myGeneration !== generation) return;

    var attempts = retryCounts[key] || 0;
    if (attempts >= RETRY_LIMIT) {
      finished = true;
      nextHref = null;
      showLoader(false);
      if (observer) { observer.disconnect(); observer = null; }
      showNativePagination();
      return;
    }

    retryCounts[key] = attempts + 1;
    setTimeout(function () {
      if (myGeneration !== generation) return;
      delete fetched[key];
      loadMore();
    }, RETRY_DELAY_MS * (attempts + 1));
  }

  /* Neutralise Dawn's scroll-reveal on appended nodes so they show
     immediately. The page-load reveal observer (animations.js) never
     sees nodes added later, so we mirror facets.js / product-info.js:
     add scroll-trigger--cancel and clear scroll-trigger--offscreen. */
  function revealNow(node) {
    if (node.classList && node.classList.contains('scroll-trigger')) {
      node.classList.remove('scroll-trigger--offscreen');
      node.classList.add('scroll-trigger--cancel');
    }
    if (node.querySelectorAll) {
      node.querySelectorAll('.scroll-trigger').forEach(function (n) {
        n.classList.remove('scroll-trigger--offscreen');
        n.classList.add('scroll-trigger--cancel');
      });
    }
  }

  /* ── fetch & append ──────────────────────────────────── */

  function loadMore() {
    if (isLoading || finished || !nextHref) return;

    var url = nextHref;
    var myGeneration = generation;
    var key = myGeneration + '|' + url;
    if (fetched[key]) return;             // never fetch the same page twice

    isLoading = true;
    fetched[key] = true;
    showLoader(true);

    // Abort any previous in-flight request (e.g. a filter changed mid-fetch).
    if (controller) controller.abort();
    controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;

    fetch(url, controller ? { signal: controller.signal } : {})
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (myGeneration !== generation) return; // stale grid — discard silently

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var state = readState(doc);
        var requestedPage = requestedPageFromUrl(url);
        var pageMatches = requestedPage === null || !state.valid || state.currentPage === requestedPage;
        var isEOF = state.valid && state.currentPage >= state.totalPages;
        var isConsistent = state.valid && pageMatches && (isEOF || state.nextUrl);

        if (!isConsistent) {
          handleRecoverable(myGeneration, key);
          return;
        }

        // Append only the new product <li> items.
        var g = gridEl();
        var incoming = doc.querySelectorAll('#product-grid > li');
        if (g && incoming.length) {
          var frag = document.createDocumentFragment();
          incoming.forEach(function (li) {
            var clone = li.cloneNode(true);
            revealNow(clone);
            frag.appendChild(clone);
          });
          g.appendChild(frag);
        }

        retryCounts[key] = 0;
        nextHref = isEOF ? null : state.nextUrl;
      })
      .catch(function (err) {
        if (myGeneration !== generation) return;
        if (err && err.name === 'AbortError') return;
        console.warn('[Wand] Infinite scroll fetch failed:', err);
        handleRecoverable(myGeneration, key);
      })
      .finally(function () {
        if (myGeneration !== generation) return;
        isLoading = false;
        showLoader(false);
        if (finished) return;
        if (!nextHref) {
          finish();
        } else {
          // A short page may have left the trigger still on-screen;
          // re-observe to force a fresh intersection check.
          reobserve();
        }
      });
  }

  /* ── IntersectionObserver ────────────────────────────── */

  function reobserve() {
    var t = triggerEl();
    if (!observer || !t) return;
    observer.unobserve(t);
    observer.observe(t);                  // re-fires the callback with current state
  }

  function attachObserver() {
    if (observer) { observer.disconnect(); observer = null; }
    var t = triggerEl();
    if (!t) return;

    observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '600px 0px' });

    observer.observe(t);
  }

  /* ── init ────────────────────────────────────────────── */

  function init() {
    generation++;

    isLoading = false;
    finished = false;
    fetched = Object.create(null);
    retryCounts = Object.create(null);
    if (controller) { controller.abort(); controller = null; }
    if (observer) { observer.disconnect(); observer = null; }

    if (!gridEl() || !triggerEl()) return;

    var state = readState(document);
    if (!state.valid) return;

    nextHref = state.currentPage >= state.totalPages ? null : state.nextUrl;
    if (!nextHref) return;                // single / last page — keep fallback pagination visible

    hidePagination();                     // hide page numbers only when infinite scroll is active
    attachObserver();
  }

  /* ── re-init after filter / sort change (facets.js) ─── */

  function watchFacets() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var mo = new MutationObserver(function () {
      // Facets replaced the grid — abort any stale load, then reset & re-init
      // synchronously (no setTimeout) so a stale fetch has no window to land
      // in between the DOM swap and the generation bump.
      if (controller) { controller.abort(); controller = null; }
      var end = document.querySelector('.wand-infinite-end');
      if (end) end.remove();
      init();
    });

    // subtree:false → only direct-child swaps of #ProductGridContainer
    // (facets replaces its innerHTML) fire this; our own appends to the
    // #product-grid grandchild do not, so there is no feedback loop.
    mo.observe(container, { childList: true, subtree: false });
  }

  /* ── bootstrap ───────────────────────────────────────── */

  function bootstrap() {
    init();
    watchFacets();

    // Mobile Safari (and other browsers with bfcache) can restore this page
    // from cache without re-running scripts, leaving stale closure state
    // (e.g. isLoading stuck true from an in-flight fetch at navigation-away
    // time). Force a clean reset whenever the page is restored this way.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
